import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { Modal, ScheduleView } from '../../../../components'
import { usePeerGroups } from '../../../../providers/PeerGroups/PeerGroups.provider'
import { useSchedule } from '../../../../providers/Schedule/Schedule.provider'
import {
  Appointment,
  Patient,
  ScheduleDay,
} from '../../../../providers/Schedule/types'
import { themeColors } from '../../../../theme'
import { dateFormatters } from '../../../../utilities/HelperFunctions'
import { AddPatientToGroupModal } from './AddPatientToGroupModal'
import { ScheduleDetailView } from './ScheduleDetail.view'

const Schedule = () => {
  const [activeDay, setActiveDay] = useState<Date | null>(null)
  const [schedule, setSchedule] = useState<ScheduleDay[]>([])
  const [isDetailView, setIsDetailView] = useState<boolean>(false)
  const [showCancelModalMode, setShowCancelModalMode] = useState<boolean>(false)
  const [showRemovePatientModalMode, setShowRemovePatientModalMode] =
    useState<boolean>(false)

  const [addPatientModalOpen, setAddPatientModalOpen] = useState<boolean>(false)

  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null)

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null) // Use Patient or Participant

  const [addingPatient, setAddingPatient] = useState<boolean>(false)
  const [removingPatient, setRemovingPatient] = useState<boolean>(false)

  const {
    sevenDayAppointments,
    daysWithAppointments,
    get7dayAppointments,
    getDaysWithAppointments,
    hideAppointment,
    removePatientFromCurrentSession,
    removePatientFromAllSessions,
    scheduleMode,
  } = useSchedule()

  const { deletePeerGroupSession, deletePeerGroup } = usePeerGroups()

  useEffect(() => {
    setActiveDay(new Date())
  }, [])

  useEffect(() => {
    if (selectedAppointment) {
      const selectedAppointmentReturn = sevenDayAppointments.find(
        (element) => element.appointmentId === selectedAppointment.appointmentId
      )
      setSelectedAppointment(selectedAppointmentReturn!)
    }
  }, [sevenDayAppointments])

  useEffect(() => {
    if (activeDay) {
      get7dayAppointments(activeDay)
      getDaysWithAppointments(activeDay.getMonth(), activeDay.getFullYear())
      const scheduleUpdater = setInterval(() => {
        get7dayAppointments(activeDay)
        getDaysWithAppointments(activeDay.getMonth(), activeDay.getFullYear())
      }, 15000)

      return () => clearInterval(scheduleUpdater)
    }
  }, [activeDay, scheduleMode])

  useEffect(() => {
    setIsDetailView(!!selectedAppointment)
  }, [selectedAppointment])

  useEffect(() => {
    // GROUP BY DAY
    const scheduleByDay = sevenDayAppointments.reduce((r, a) => {
      const day = new Date(a.startTime).toDateString()

      const foundAt = r.findIndex((sd: ScheduleDay) => sd.day === day)
      const foundDayOrNew: ScheduleDay =
        foundAt > -1 ? r[foundAt] : { day, appointments: [] }
      foundDayOrNew.appointments.push(a)

      if (foundAt === -1) {
        r.push(foundDayOrNew)
      }
      return r
    }, [] as ScheduleDay[])
    setSchedule(scheduleByDay)
  }, [sevenDayAppointments])

  const removeEventFromCalendar = async (appointmentId: number) => {
    if (activeDay) {
      await hideAppointment(appointmentId)
      get7dayAppointments(activeDay)
    }
  }

  const selectAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment)
  }

  const selectPatient = (patient: Patient) => {
    setSelectedPatient(patient)
  }

  const returnToListView = () => {
    setSelectedAppointment(null)
  }

  const handleCancelSession = async () => {
    if (selectedAppointment?.appointmentId && selectedAppointment.peerGroupId) {
      if (selectedAppointment.isRecurring) {
        deletePeerGroupSession(selectedAppointment?.appointmentId)
      } else {
        deletePeerGroup(selectedAppointment.peerGroupId)
      }
    }
    setActiveDay(new Date())
    returnToListView()
  }

  const handleRemovePatientSession = async (
    selectedPatient: Patient,
    selectedAppointment: Appointment,
    removeAll: boolean
  ) => {
    setRemovingPatient(true)
    if (removeAll) {
      await removePatientFromAllSessions(
        selectedPatient.participantId,
        selectedAppointment.appointmentId
      )
    } else {
      await removePatientFromCurrentSession(
        selectedPatient.participantId,
        selectedAppointment.appointmentId
      )
    }
    await get7dayAppointments(activeDay!)
    setRemovingPatient(false)
  }

  interface CancelModalProps {
    selectedAppointment: Appointment
    setShowCancelModalMode: Dispatch<SetStateAction<boolean>>
  }

  const CancelModal = ({
    selectedAppointment,
    setShowCancelModalMode,
  }: CancelModalProps) => (
    <Modal
      open={showCancelModalMode}
      setOpen={setShowCancelModalMode}
      title={'CANCEL PEER GROUP MEETING'}
      width={'30%'}
      ModalContent={
        <Box sx={{ width: '92%', margin: 'auto' }}>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '19px',
              color: themeColors.grayblack,
            }}
          >
            Are you sure you want to cancel this peer group meeting for{' '}
            {dateFormatters.prettyDate(selectedAppointment.startTime)}?
          </Typography>
          {!selectedAppointment.isRecurring && (
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
                paddingBottom: '10px',
                paddingTop: '10px',
              }}
            >
              NOTE: If cancelled, the meeting will be removed from all
              participant schedules as well as the Peer Group List view.
            </Typography>
          )}
          <Box sx={{ display: 'flex', margin: '7px', alignItems: 'center' }}>
            <Button
              variant='contained'
              onClick={() => setShowCancelModalMode(false)}
              sx={{ marginRight: '10px' }}
            >
              No
            </Button>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
              }}
            >
              Close window and return to previous page
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', margin: '7px', alignItems: 'center' }}>
            <Button
              variant='outlined'
              color='error'
              onClick={() => {
                setShowCancelModalMode(false)
                handleCancelSession()
              }}
              sx={{ marginRight: '10px' }}
            >
              Yes
            </Button>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
              }}
            >
              Cancel peer group meeting
            </Typography>
          </Box>
        </Box>
      }
    />
  )

  interface RemovePatientModalProps {
    selectedAppointment: Appointment
    setShowRemovePatientModalMode: Dispatch<SetStateAction<boolean>>
    selectedPatient: Patient
  }

  const RemovePatientModal = ({
    selectedAppointment,
    setShowRemovePatientModalMode,
    selectedPatient,
  }: RemovePatientModalProps) => (
    <Modal
      open={showRemovePatientModalMode}
      setOpen={setShowRemovePatientModalMode}
      title={'REMOVE PATIENT FROM SESSION'}
      width={'30%'}
      ModalContent={
        <Box sx={{ width: '92%', margin: 'auto' }}>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '19px',
              color: themeColors.grayblack,
            }}
          >
            Are you sure you want to remove this patient from this session of{' '}
            {selectedAppointment.appointmentName}?
          </Typography>
          {!selectedAppointment.isRecurring && (
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
                paddingBottom: '10px',
                paddingTop: '10px',
              }}
            >
              {/* NOTE: If cancelled, the meeting will be removed from all
              participant schedules as well as the Peer Group List view. */}
              NOTE: If participant is removed from this session
            </Typography>
          )}
          <Box sx={{ display: 'flex', margin: '7px', alignItems: 'center' }}>
            <Button
              variant='contained'
              onClick={() => setShowRemovePatientModalMode(false)}
              sx={{ marginRight: '10px' }}
            >
              No
            </Button>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
              }}
            >
              Close window and return to previous page
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', margin: '7px', alignItems: 'center' }}>
            <Button
              variant='outlined'
              color='error'
              onClick={() => {
                setShowRemovePatientModalMode(false)
                handleRemovePatientSession(
                  selectedPatient,
                  selectedAppointment,
                  false
                )
              }}
              sx={{ marginRight: '10px' }}
            >
              Yes Current:
            </Button>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
              }}
            >
              Remove {selectedPatient.name} from this session of{' '}
              {selectedAppointment.name}?
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', margin: '7px', alignItems: 'center' }}>
            <Button
              variant='outlined'
              color='error'
              onClick={() => {
                setShowRemovePatientModalMode(false)
                handleRemovePatientSession(
                  selectedPatient,
                  selectedAppointment,
                  true
                )
              }}
              sx={{ marginRight: '10px' }}
            >
              Yes All:
            </Button>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '17px',
                color: themeColors.gray20,
              }}
            >
              Remove {selectedPatient.name} from this and all future sessions of{' '}
              {selectedAppointment.name}?
            </Typography>
          </Box>
        </Box>
      }
    />
  )

  return !(selectedAppointment && isDetailView) ? (
    <ScheduleView
      activeDay={activeDay}
      scheduleMode={scheduleMode}
      schedule={schedule}
      daysWithAppointments={daysWithAppointments}
      setActiveDay={setActiveDay}
      removeEventFromCalendar={removeEventFromCalendar}
      selectAppointment={async (appointment) => {
        await selectAppointment(appointment as Appointment)
        return true
      }}
    />
  ) : (
    <Box style={{ padding: '0 10px', height: '100%' }}>
      {(addingPatient || removingPatient) && (
        <CircularProgress
          size={20}
          color='primary'
          style={{ position: 'fixed', margin: '220px' }}
        />
      )}

      <ScheduleDetailView
        returnToListView={returnToListView}
        appointment={selectedAppointment!}
        setShowCancelModalMode={setShowCancelModalMode}
        setShowRemovePatientModalMode={setShowRemovePatientModalMode}
        selectPatient={selectPatient}
        removingPatient={removingPatient}
        addPatientModalOpen={addPatientModalOpen}
        setAddPatientModalOpen={setAddPatientModalOpen}
        activeDay={activeDay}
      />

      <CancelModal {...{ selectedAppointment, setShowCancelModalMode }} />

      {addPatientModalOpen && (
        <AddPatientToGroupModal
          showAddPatientModal={addPatientModalOpen}
          setShowAddPatientModal={setAddPatientModalOpen}
          peerGroupId={selectedAppointment.peerGroupId}
          peerGroupSessionId={selectedAppointment.id}
          setAddingPatient={setAddingPatient}
          activeDay={activeDay}
        />
      )}

      {selectedPatient && (
        <RemovePatientModal
          {...{
            selectedAppointment,
            setShowRemovePatientModalMode,
            selectedPatient,
          }}
        />
      )}
    </Box>
  )
}

export { Schedule }
